import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { useT, useDocumentTitleKey } from "@/i18n/locale-context";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [{ title: "Nouveau mot de passe — Glow Up Coach" }] }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  useDocumentTitleKey("reset.head.title");
  const t = useT();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);

  useEffect(() => {
    // Supabase parses the recovery token from the URL hash and emits
    // a PASSWORD_RECOVERY event, creating a temporary session that
    // authorizes updateUser({ password }).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
          setHasRecoverySession(true);
        }
      },
    );
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setHasRecoverySession(true);
      else setHasRecoverySession((prev) => prev ?? false);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success(t("reset.toast.updated"));
      nav({ to: "/app" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        <h1
          className="font-display font-bold text-3xl mb-2 tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("reset.title")}
        </h1>
        <p className="text-muted-foreground text-sm mb-8">{t("reset.subtitle")}</p>

        {hasRecoverySession === false ? (
          <div className="rounded-xl border border-foreground/15 p-4 text-sm text-muted-foreground">
            {t("reset.invalid_link")}
            <Link
              to="/forgot-password"
              className="block mt-4 text-foreground hover:underline"
            >
              {t("login.forgot_password")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <input
              type="password"
              required
              minLength={8}
              placeholder={t("reset.input.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-foreground/15 bg-background text-sm focus:outline-none focus:border-accent"
            />
            <button
              type="submit"
              disabled={loading || !hasRecoverySession}
              className="w-full bg-foreground text-background py-3 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "…" : t("reset.btn.update")}
            </button>
          </form>
        )}

        <Link
          to="/login"
          className="block text-center text-xs text-muted-foreground hover:text-foreground mt-8"
        >
          {t("forgot.back_login")}
        </Link>
      </div>
    </div>
  );
}